import type { UseFernieChatReturn } from "~/hooks/useFernieChat";
import type { FernieRequestsPayload } from "~/services/aiOrchestrator";

import { useCallback, useEffect, useRef, useState } from "react";

import logger from "~/helpers/logger";
import { fetchFernieRequests } from "~/services/aiOrchestrator";

type Availability = "unknown" | "available" | "disabled";

export interface UseFernieRequestsReturn {
	error: boolean;
	isAvailable: boolean;
	loading: boolean;
	payload: FernieRequestsPayload | null;
	retry: () => void;
}

const useFernieRequests = (chat: UseFernieChatReturn, active: boolean, isEnabled: boolean): UseFernieRequestsReturn => {
	const [payload, setPayload] = useState<FernieRequestsPayload | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [availability, setAvailability] = useState<Availability>("unknown");
	const latestRead = useRef(0);
	const previousView = useRef(chat.view);
	const { setView, view } = chat;
	const isPanelOpenWithFlag = isEnabled && active;

	const load = useCallback(() => {
		const readId = ++latestRead.current;
		setLoading(true);
		setError(false);
		void Promise.resolve()
			.then(() => fetchFernieRequests())
			.then((result) => {
				if (readId !== latestRead.current) return;
				setPayload(result);
				setAvailability(result.enabled ? "available" : "disabled");
			})
			.catch((cause: unknown) => {
				if (readId !== latestRead.current) return;
				logger.error("Fernie requests could not be loaded", cause);
				setError(true);
				setAvailability((current) => (current === "unknown" ? "available" : current));
			})
			.finally(() => {
				if (readId === latestRead.current) setLoading(false);
			});
	}, []);

	useEffect(
		() => () => {
			latestRead.current += 1;
		},
		[]
	);

	useEffect(() => {
		if (isPanelOpenWithFlag) load();
	}, [isPanelOpenWithFlag, load]);

	useEffect(() => {
		const isAlreadyOnRequests = previousView.current === "requests";
		previousView.current = view;
		if (isPanelOpenWithFlag && view === "requests" && !isAlreadyOnRequests) load();
	}, [view, isPanelOpenWithFlag, load]);

	useEffect(() => {
		if ((!isEnabled || availability === "disabled") && view === "requests") setView("chat");
	}, [isEnabled, availability, view, setView]);

	return {
		error,
		isAvailable: isEnabled && availability === "available",
		loading,
		payload,
		retry: load
	};
};

export default useFernieRequests;
